import { Helmet } from "react-helmet-async";

const SITE = "https://borra-me.com";
const DEFAULT_OG_IMAGE = `${SITE}/og-image.png`;

/**
 * <SEO /> — single source of truth for per-route <head>.
 *
 * Renders <title>, meta description, canonical, Open Graph, Twitter tags,
 * and up to N JSON-LD schema blocks (Article, FAQPage, HowTo, Product…).
 *
 * IMPORTANT: this only manages runtime <head>. For crawlers without JS,
 * the postbuild prerender script `scripts/prerender-static.js` bakes the
 * same values into per-route static HTML files.
 */
export default function SEO({
  title,
  description,
  canonical,          // absolute or relative path
  ogImage,
  ogType = "website",
  keywords,
  noindex = false,
  schemas = [],       // array of JSON-LD schema objects
}) {
  const canonicalUrl = canonical
    ? (canonical.startsWith("http") ? canonical : `${SITE}${canonical}`)
    : undefined;
  const imgUrl = ogImage
    ? (ogImage.startsWith("http") ? ogImage : `${SITE}${ogImage}`)
    : DEFAULT_OG_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="es_CL" />
      <meta property="og:site_name" content="borra-me.com" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={imgUrl} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imgUrl} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

export const SITE_ORIGIN = SITE;
